import React from "react";
import { FONT_FAMILY, OVERVIEW } from "../../constants/theme";
import { classifyHealth } from "../../utils/scoring";
import { healthColor } from "../../utils/colors";
import Description from "../typography/Description";

const HEALTH_LABELS = {
  healthy: "HEALTHY",
  attention: "WATCHLIST",
  "at-risk": "AT RISK",
  critical: "CRITICAL",
};

function getStatusLine(criticalCount, ageingCount, idleCount) {
  if (criticalCount > 0 && ageingCount > 0)
    return `${criticalCount} critical ticket${criticalCount !== 1 ? "s" : ""} require review. Ageing backlog continues to increase.`;
  if (criticalCount > 0)
    return `${criticalCount} critical ticket${criticalCount !== 1 ? "s" : ""} require immediate review.`;
  if (ageingCount > 0)
    return `${ageingCount} ticket${ageingCount !== 1 ? "s" : ""} ageing past 14 days. Review recommended.`;
  if (idleCount > 0)
    return `${idleCount} ticket${idleCount !== 1 ? "s" : ""} with no recent activity.`;
  return "Sprint health looks good. No critical issues detected.";
}

function SkeletonBlock({ width, height }) {
  return (
    <span style={{
      display: "inline-block", width, height,
      background: "rgba(255,255,255,0.07)", borderRadius: 2, verticalAlign: "middle",
    }} />
  );
}

function ActionCard({ label, sub, icon, href }) {
  const [hov, setHov] = React.useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex: 1,
        background: hov ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.04)",
        border: hov ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(255,255,255,0.09)",
        borderRadius: 8,
        padding: "24px 28px",
        cursor: "pointer",
        transition: "all 0.14s",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        textDecoration: "none",
      }}
    >
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
        {icon}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: hov ? "#FFFFFF" : "rgba(255,255,255,0.75)", letterSpacing: "-0.01em" }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", lineHeight: 1.5 }}>
        {sub}
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: hov ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.2)", transition: "color 0.14s" }}>
        →
      </div>
    </a>
  );
}

export default function TheBrief({
  sprint,
  ticketCount,
  criticalCount,
  ageingCount,
  idleCount,
  loading,
  developers = [],
  selectedDeveloper = "",
  onSelectDeveloper,
}) {
  const health = classifyHealth({ criticalCount, ageingCount, totalOpen: ticketCount });
  const { text: healthText } = healthColor(health);
  const healthWord = HEALTH_LABELS[health] || "HEALTHY";
  const sprintShort = sprint ? sprint.split("·")[0].trim() : "Current Sprint";

  const developerNames = [
    ...new Set(developers.map((d) => d && d.assignedTo).filter(Boolean)),
  ];

  return (
    <section style={{
      background: "#071A2F",
      fontFamily: FONT_FAMILY,
      paddingBottom: 56,
    }}>
      <div style={{
        maxWidth: OVERVIEW.contentMax,
        margin: "0 auto",
        padding: `72px ${OVERVIEW.gutter}px 0`,
        boxSizing: "border-box",
        display: "flex",
        gap: 48,
        alignItems: "flex-start",
        minHeight: 460,
      }}>

        {/* LEFT — 70% */}
        <div style={{ flex: "0 0 70%", minWidth: 0 }}>

          {/* Overline + health badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)" }}>
              {sprintShort} · Engineering Operations
            </div>
            <span style={{
              padding: "3px 10px", borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.06)",
              color: healthText, fontSize: 11, fontWeight: 600,
              letterSpacing: "0.08em", textTransform: "uppercase", flexShrink: 0,
            }}>
              {healthWord}
            </span>
          </div>
          <Description light style={{ marginBottom: 24, maxWidth: 520 }}>
            Overall sprint status, based on how many tickets are critical and how much of the open backlog is ageing past 14 days.
          </Description>

          {/* Hero title */}
          <div style={{
            fontSize: 66, fontWeight: 700, letterSpacing: "-0.03em",
            lineHeight: 1.0, color: "#FFFFFF", marginBottom: 22,
          }}>
            {loading
              ? <SkeletonBlock width={420} height={66} />
              : <>Engineering<br />Operations Hub</>}
          </div>

          {/* Mission statement */}
          <div style={{
            fontSize: 18, fontWeight: 400,
            color: "rgba(255,255,255,0.45)",
            lineHeight: 1.65, marginBottom: 40, maxWidth: 560,
          }}>
            A single view of delivery health, operational risk, and execution priorities across the engineering organisation.
          </div>

          {/* Personalization — primary "Viewing As" control */}
          <div data-tour="viewing-as">
            <label
              htmlFor="overview-developer-select"
              style={{
                display: "block",
                fontSize: 13, fontWeight: 700,
                letterSpacing: "0.14em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.5)",
                marginBottom: 12,
              }}
            >
              Viewing As
            </label>
            <div style={{ position: "relative", width: "100%" }}>
              <select
                id="overview-developer-select"
                value={selectedDeveloper}
                onChange={(e) => onSelectDeveloper && onSelectDeveloper(e.target.value)}
                style={{
                  width: "100%",
                  padding: "18px 52px 18px 22px",
                  borderRadius: 10,
                  border: `1px solid ${selectedDeveloper ? "rgba(147,197,253,0.55)" : "rgba(255,255,255,0.18)"}`,
                  background: "rgba(255,255,255,0.06)",
                  color: "#FFFFFF",
                  fontSize: 20, fontWeight: 700,
                  fontFamily: FONT_FAMILY,
                  letterSpacing: "-0.01em",
                  cursor: "pointer",
                  outline: "none",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                }}
              >
                <option value="" style={{ background: "#071A2F", color: "#FFFFFF" }}>All Developers</option>
                {developerNames.map((name) => (
                  <option key={name} value={name} style={{ background: "#071A2F", color: "#FFFFFF" }}>{name}</option>
                ))}
              </select>
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: 22,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  fontSize: 14,
                  color: "rgba(255,255,255,0.55)",
                }}
              >
                ▼
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT — 30% action panel */}
        <div style={{ flex: 1, paddingTop: 4, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.2)", marginBottom: 8 }}>
            Quick Access
          </div>
          <ActionCard
            label="Open Jira"
            sub="Review, triage, and manage tickets"
            icon="JIRA"
            href="https://ecosense-enviro.atlassian.net/browse"
          />
          <ActionCard
            label="Open GitHub"
            sub="View repositories and pull requests"
            icon="GITHUB"
            href="https://github.com/EcoSense-Enviro"
          />
          <div style={{ marginTop: 16, padding: "16px 20px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.2)", marginBottom: 10 }}>
              Sprint Status
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", lineHeight: 1.65 }}>
              {loading ? <SkeletonBlock width={180} height={12} /> : getStatusLine(criticalCount, ageingCount, idleCount)}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
